import React, { useRef, useState } from "react";
import axios from "axios";
import { Send } from "lucide-react";
import { useLang } from "@/i18n";
import { CONTACT_EMAIL } from "@/lib/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const empty = { name: "", business: "", email: "", phone: "", message: "", consent: false, hp: "" };

/**
 * Real enquiry form. The backend owns the recipient and the template, so the
 * only thing travelling from here is the visitor's own data.
 */
export const ContactForm = ({
  kind = "general",
  tone = "light",
  showBusiness = false,
  id,
  testId = "contact-form",
}) => {
  const { t } = useLang();
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [statusMsg, setStatusMsg] = useState("");
  const startedAt = useRef(Date.now());
  const dark = tone === "dark";

  const set = (key) => (e) => {
    const value = key === "consent" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = t.form.errors.name;
    if (!EMAIL_RE.test(form.email.trim())) next.email = t.form.errors.email;
    if (form.message.trim().length < 10) next.message = t.form.errors.message;
    if (!form.consent) next.consent = t.form.errors.consent;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    if (!validate()) {
      setStatus("invalid");
      setStatusMsg("");
      return;
    }
    setStatus("sending");
    setStatusMsg(t.form.sending);
    try {
      await axios.post(`${API}/contact`, {
        kind,
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        business: form.business.trim() || null,
        phone: form.phone.trim() || null,
        consent: true,
        // bots fill hidden fields and submit instantly
        hp: form.hp || (Date.now() - startedAt.current < 1200 ? "fast" : ""),
      });
      setForm(empty);
      setStatus("sent");
      setStatusMsg(t.form.sent);
    } catch (err) {
      const rate = err?.response?.status === 429;
      setStatus("error");
      setStatusMsg(rate ? t.form.rate : `${t.form.error} ${CONTACT_EMAIL}`);
    }
  };

  const fieldClass = (key) =>
    `w-full min-h-[48px] rounded-xl px-4 py-3 transition-colors focus:outline-none focus:ring-2 ${
      dark
        ? "bg-[#F5F1E8]/[0.06] text-[#F5F1E8] placeholder:text-[#F5F1E8]/45 focus:ring-[#F4D35E]"
        : "bg-white text-[#3B2523] placeholder:text-[#3B2523]/45 focus:ring-[#3B2523]"
    } border ${
      errors[key]
        ? "border-[#E06A54]"
        : dark
          ? "border-[#F5F1E8]/25 focus:border-[#F4D35E]"
          : "border-[#3B2523]/25 focus:border-[#3B2523]"
    }`;

  const labelClass = `block text-xs font-semibold uppercase tracking-widest mb-2 ${
    dark ? "text-[#E6D9B6]/80" : "text-[#3B2523]/70"
  }`;

  const errorClass = `text-sm mt-2 ${dark ? "text-[#FFB4A0]" : "text-[#8C2F1E]"}`;

  const field = (key, type = "text") => (
    <div>
      <label htmlFor={`${testId}-${key}`} className={labelClass}>
        {t.form[key]}
      </label>
      <input
        id={`${testId}-${key}`}
        data-testid={`${testId}-${key}`}
        type={type}
        value={form[key]}
        onChange={set(key)}
        aria-invalid={!!errors[key]}
        aria-describedby={errors[key] ? `${testId}-${key}-error` : undefined}
        className={fieldClass(key)}
      />
      {errors[key] && (
        <p id={`${testId}-${key}-error`} className={errorClass}>
          {errors[key]}
        </p>
      )}
    </div>
  );

  return (
    <form
      id={id}
      data-testid={testId}
      onSubmit={submit}
      noValidate
      className="flex flex-col gap-5 scroll-mt-[120px]"
    >
      {field("name")}
      {showBusiness && field("business")}
      {field("email", "email")}
      {field("phone", "tel")}

      <div>
        <label htmlFor={`${testId}-message`} className={labelClass}>
          {t.form.message}
        </label>
        <textarea
          id={`${testId}-message`}
          data-testid={`${testId}-message`}
          rows={4}
          value={form.message}
          onChange={set("message")}
          aria-invalid={!!errors.message}
          aria-describedby={errors.message ? `${testId}-message-error` : undefined}
          className={`${fieldClass("message")} resize-y`}
        />
        {errors.message && (
          <p id={`${testId}-message-error`} className={errorClass}>
            {errors.message}
          </p>
        )}
      </div>

      <div>
        <label className={`flex items-start gap-3 cursor-pointer ${dark ? "text-[#E6D9B6]/80" : "text-[#3B2523]/70"}`}>
          <input
            type="checkbox"
            data-testid={`${testId}-consent`}
            checked={form.consent}
            onChange={set("consent")}
            aria-invalid={!!errors.consent}
            aria-describedby={errors.consent ? `${testId}-consent-error` : undefined}
            className="mt-1 w-5 h-5 shrink-0 accent-[#F4D35E]"
          />
          <span className="text-sm leading-relaxed">{t.form.consent}</span>
        </label>
        {errors.consent && (
          <p id={`${testId}-consent-error`} className={errorClass}>
            {errors.consent}
          </p>
        )}
      </div>

      {/* honeypot: invisible to people, irresistible to bots */}
      <input
        type="text"
        name="company_website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={form.hp}
        onChange={set("hp")}
        className="absolute w-px h-px -left-[9999px] opacity-0"
      />

      <button
        type="submit"
        data-testid={`${testId}-submit`}
        disabled={status === "sending"}
        className={`self-start inline-flex items-center gap-2.5 min-h-[48px] px-8 py-4 rounded-full font-semibold transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
          dark
            ? "bg-[#F4D35E] text-[#3B2523] focus-visible:ring-[#F4D35E] focus-visible:ring-offset-[#3B2523]"
            : "bg-[#3B2523] text-[#F5F1E8] focus-visible:ring-[#3B2523] focus-visible:ring-offset-[#F5F1E8]"
        }`}
      >
        <Send size={17} />
        {status === "sending" ? t.form.sending : t.form.submit}
      </button>

      <div aria-live="polite" role="status" data-testid={`${testId}-status`}>
        {statusMsg && (
          <p
            className={`text-sm leading-relaxed ${
              status === "error"
                ? dark
                  ? "text-[#FFB4A0]"
                  : "text-[#8C2F1E]"
                : dark
                  ? "text-[#F4D35E]"
                  : "text-[#3B2523]"
            }`}
          >
            {statusMsg}
          </p>
        )}
      </div>

      <p className={`text-sm ${dark ? "text-[#E6D9B6]/70" : "text-[#3B2523]/70"}`}>
        {t.form.altEmail}{" "}
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          data-testid={`${testId}-mailto`}
          className={`font-semibold underline underline-offset-4 ${dark ? "text-[#F4D35E]" : "text-[#3B2523]"}`}
        >
          {CONTACT_EMAIL}
        </a>
      </p>
    </form>
  );
};

export default ContactForm;
